import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import { BACKEND_URL } from './config';
import Footer from './Footer'; // Import the new Footer component
import './App.css';

// Set Axios default to always send credentials
axios.defaults.withCredentials = true;

const SocialMediaScheduler = ({ onLogin }) => {
  const [authUrls, setAuthUrls] = useState({ tiktok: '', instagram: '', twitter: '', youtube: '' });
  const [loginStatus, setLoginStatus] = useState({ tiktok: false, instagram: false, facebook: false, twitter: false, youtube: false });
  const [userId, setUserId] = useState(null);
  const [userDetails, setUserDetails] = useState({ tiktok: null, instagram: null, twitter: null, youtube: null });
  const [validationErrors, setValidationErrors] = useState({ tiktok: null, instagram: null, twitter: null, youtube: null });
  const [needsReconnect, setNeedsReconnect] = useState({ tiktok: false, instagram: false, facebook: false, twitter: false, youtube: false });
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaUrl, setMediaUrl] = useState('');
  const [caption, setCaption] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  const [platforms, setPlatforms] = useState([]);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState(''); // 'success' or 'error'
  const [isDragging, setIsDragging] = useState(false);
  const [previewMedia, setPreviewMedia] = useState(null);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [mediaType, setMediaType] = useState(null);
  const [imageEdits, setImageEdits] = useState({ rotate: 0, filter: 'none', crop: { x: 0, y: 0, width: 100, height: 100 } });
  const [videoEdits, setVideoEdits] = useState({ start: 0, end: null });
  const [videoDuration, setVideoDuration] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [view, setView] = useState('login');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [formErrors, setFormErrors] = useState({ username: '', email: '', password: '', newPassword: '' });

  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const previewMediaRef = useRef(null);
  const navigate = useNavigate();

  // Input validation functions
  const validateUsername = (username) => {
    if (!username) return 'Username is required';
    if (username.length < 3) return 'Username must be at least 3 characters long';
    if (!/^[a-zA-Z0-9_]+$/.test(username)) return 'Username can only contain letters, numbers, and underscores';
    return '';
  };

  const validateEmail = (email) => {
    if (!email) return 'Email is required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return 'Invalid email format';
    return '';
  };

  const validatePassword = (password) => {
    if (!password) return 'Password is required';
    if (password.length < 6) return 'Password must be at least 6 characters long';
    if (!/[A-Z]/.test(password)) return 'Password must contain at least one uppercase letter';
    if (!/[0-9]/.test(password)) return 'Password must contain at least one number';
    return '';
  };

  // Set message with type
  const setMessageWithType = (msg, type) => {
    setMessage(msg);
    setMessageType(type);
  };

  // Add a retry utility function
  const retry = async (fn, retries = 3, delay = 1000) => {
    for (let i = 0; i < retries; i++) {
      try {
        return await fn();
      } catch (error) {
        if (i === retries - 1) throw error;
        console.log(`Retry attempt ${i + 1} failed, retrying in ${delay}ms...`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  };

  // Check app login status on mount
  useEffect(() => {
    const checkAppLoginStatus = async () => {
      try {
        setIsLoading(true);
        console.log('Checking app login status...');
        // Add a slight delay to ensure the session is saved
        await new Promise(resolve => setTimeout(resolve, 1000));
        const res = await retry(() =>
          axios.get(`${BACKEND_URL}/api/check-login`, { withCredentials: true })
        );
        console.log('App login status response:', {
          userId: res.data.userId,
          status: res.status,
          headers: res.headers,
        });
        setUserId(res.data.userId || null);
        if (!res.data.userId) {
          console.log('No userId in response, user is logged out');
        }
      } catch (error) {
        console.error('Error checking app login status:', {
          message: error.message,
          code: error.code,
          response: error.response ? {
            status: error.response.status,
            data: error.response.data,
            headers: error.response.headers,
          } : null,
        });
        if (error.response?.status === 401) {
          console.log('Session is invalid (401), logging out user');
          setUserId(null);
        } else {
          console.log('Network or server error, preserving current userId:', userId);
          setMessageWithType(
            error.code === 'ERR_NETWORK'
              ? 'Cannot connect to the server. Please ensure the backend is running.'
              : 'Error checking login status: ' + (error.message || 'Unknown error'),
            'error'
          );
        }
      } finally {
        setIsLoading(false);
      }
    };

    checkAppLoginStatus();
  }, []);

  // Fetch auth URLs after user login
  useEffect(() => {
    if (!userId) {
      console.log('User not logged in, skipping auth URL fetch.');
      return;
    }

    const fetchAuthUrls = async () => {
      try {
        console.log('User logged in, fetching auth URLs from backend...');
        // Check session before fetching auth URLs
        const sessionCheck = await axios.get(`${BACKEND_URL}/api/check-login`, { withCredentials: true });
        if (!sessionCheck.data.userId) {
          console.log('Session lost, redirecting to login...');
          setUserId(null);
          navigate('/login');
          return;
        }

        const [tiktokRes, instagramRes, twitterRes, youtubeRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/tiktok/auth`, { withCredentials: true, timeout: 5000 }),
          axios.get(`${BACKEND_URL}/api/instagram/auth`, { withCredentials: true, timeout: 5000 }),
          axios.get(`${BACKEND_URL}/api/twitter/auth`, { withCredentials: true, timeout: 5000 }),
          axios.get(`${BACKEND_URL}/api/youtube/auth`, { withCredentials: true, timeout: 5000 }),
        ]);
        const newAuthUrls = {
          tiktok: tiktokRes.data.authUrl || '',
          instagram: instagramRes.data.authUrl || '',
          twitter: twitterRes.data.authUrl || '',
          youtube: youtubeRes.data.authUrl || '',
        };
        console.log('Fetched auth URLs:', newAuthUrls);

        const newValidationErrors = { ...validationErrors };
        if (!newAuthUrls.tiktok) newValidationErrors.tiktok = 'Failed to fetch TikTok auth URL';
        if (!newAuthUrls.instagram) newValidationErrors.instagram = 'Failed to fetch Instagram auth URL';
        if (!newAuthUrls.twitter) newValidationErrors.twitter = 'Failed to fetch Twitter auth URL';
        if (!newAuthUrls.youtube) newValidationErrors.youtube = 'Failed to fetch YouTube auth URL';
        setValidationErrors(newValidationErrors);

        setAuthUrls(newAuthUrls);
      } catch (error) {
        console.error('Error fetching auth URLs:', error);
        if (error.response?.status === 401) {
          console.log('Unauthorized, redirecting to login...');
          setUserId(null);
          navigate('/login');
        }
        setMessageWithType(
          error.code === 'ERR_NETWORK'
            ? 'Cannot connect to the server. Please ensure the backend is running.'
            : 'Error fetching auth URLs: ' + (error.message || 'Unknown error'),
          'error'
        );
        setValidationErrors({
          tiktok: 'Failed to fetch TikTok auth URL: ' + (error.message || 'Network error'),
          instagram: 'Failed to fetch Instagram auth URL: ' + (error.message || 'Network error'),
          twitter: 'Failed to fetch Twitter auth URL: ' + (error.message || 'Network error'),
          youtube: 'Failed to fetch YouTube auth URL: ' + (error.message || 'Network error'),
        });
      }
    };

    fetchAuthUrls();
  }, [userId, validationErrors, navigate]);

  // Define checkPlatformLoginStatus with useCallback
  const checkPlatformLoginStatus = useCallback(async () => {
    try {
      console.log('Checking platform login status for userId:', userId);
      if (!userId) {
        console.log('No userId, skipping platform login check');
        setLoginStatus({
          tiktok: false,
          instagram: false,
          facebook: false,
          twitter: false,
          youtube: false,
        });
        setUserDetails({
          tiktok: null,
          instagram: null,
          twitter: null,
          youtube: null,
        });
        setValidationErrors({
          tiktok: null,
          instagram: null,
          twitter: null,
          youtube: null,
        });
        setNeedsReconnect({
          tiktok: false,
          instagram: false,
          facebook: false,
          twitter: false,
          youtube: false,
        });
        return;
      }

      const res = await axios.get(`${BACKEND_URL}/api/check-platform-login`, { withCredentials: true });
      console.log('Platform login status response:', {
        data: res.data,
        status: res.status,
        headers: res.headers,
      });
      setLoginStatus(res.data.loginStatus);
      setUserDetails(res.data.userDetails);
      setValidationErrors(res.data.validationErrors);
      setNeedsReconnect(res.data.needsReconnect);
      setIsAuthenticating(false); // Reset authenticating state after check
    } catch (error) {
      console.error('Error checking platform login status:', {
        message: error.message,
        code: error.code,
        response: error.response ? {
          status: error.response.status,
          data: error.response.data,
          headers: error.response.headers,
        } : null,
      });
      setMessageWithType(
        error.code === 'ERR_NETWORK'
          ? 'Cannot connect to the server for platform authentication. Please ensure the backend is running.'
          : 'Error checking platform login status: ' + (error.message || 'Unknown error'),
        'error'
      );
      setLoginStatus({
        tiktok: false,
        instagram: false,
        facebook: false,
        twitter: false,
        youtube: false,
      });
      setUserDetails({
        tiktok: null,
        instagram: null,
        twitter: null,
        youtube: null,
      });
      setValidationErrors({
        tiktok: null,
        instagram: null,
        twitter: null,
        youtube: null,
      });
      setNeedsReconnect({
        tiktok: false,
        instagram: false,
        facebook: false,
        twitter: false,
        youtube: false,
      });
      setIsAuthenticating(false);
    }
  }, [userId]);

  // Check platform login status after userId changes
  useEffect(() => {
    if (userId) {
      checkPlatformLoginStatus();
    }
  }, [userId, checkPlatformLoginStatus]);

  // Monitor userId changes for debugging
  useEffect(() => {
    console.log('UserId changed:', userId);
    if (userId === null) {
      console.log('User has been logged out unexpectedly');
    }
  }, [userId]);

  // Check for OAuth redirect
  useEffect(() => {
    const path = window.location.pathname;
    const callbackPaths = ['/tiktok/callback', '/instagram/callback', '/twitter/callback', '/youtube/callback'];

    if (callbackPaths.some(callbackPath => path.includes(callbackPath))) {
      console.log('Detected OAuth callback, refreshing login status...');
      setIsAuthenticating(true);
      window.history.replaceState({}, document.title, '/');
      checkPlatformLoginStatus();
    }
  }, [checkPlatformLoginStatus]);

  // Check for reset token in URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    if (token) {
      setResetToken(token);
      setView('reset-password');
      window.history.replaceState({}, document.title, '/');
    }
  }, []);

  // Cleanup object URL on unmount
  useEffect(() => {
    return () => {
      if (previewMediaRef.current) {
        URL.revokeObjectURL(previewMediaRef.current);
        previewMediaRef.current = null;
      }
    };
  }, []);

  // Handle app registration
  const handleRegister = async (e) => {
    e.preventDefault();

    const usernameError = validateUsername(username);
    const emailError = validateEmail(email);
    const passwordError = validatePassword(password);

    setFormErrors({
      username: usernameError,
      email: emailError,
      password: passwordError,
      newPassword: '',
    });

    if (usernameError || emailError || passwordError) {
      setMessageWithType('Please fix the errors in the form.', 'error');
      return;
    }

    try {
      setIsLoading(true);
      setMessageWithType('', '');
      console.log('Registering user:', { username, email });
      const response = await axios.post(`${BACKEND_URL}/api/register`, { username, email, password }, { withCredentials: true });
      console.log('Register response:', response.data);
      if (response.data.success) {
        setMessageWithType('Registration successful! Please log in.', 'success');
        setView('login');
        setUsername('');
        setEmail('');
        setPassword('');
        setFormErrors({ username: '', email: '', password: '', newPassword: '' });
      } else {
        setMessageWithType(response.data.error || 'Registration failed. Please try again.', 'error');
      }
    } catch (error) {
      console.error('Error during registration:', error);
      setMessageWithType(
        error.code === 'ERR_NETWORK'
          ? 'Cannot connect to the server. Please ensure the backend is running.'
          : error.response?.data?.error || 'Error registering: Unable to connect to the server.',
        'error'
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Handle app login
  const handleAppLogin = async (e) => {
    e.preventDefault();

    const usernameError = validateUsername(username);
    const passwordError = validatePassword(password);

    setFormErrors({
      username: usernameError,
      email: '',
      password: passwordError,
      newPassword: '',
    });

    if (usernameError || passwordError) {
      setMessageWithType('Please fix the errors in the form.', 'error');
      return;
    }

    try {
      setIsLoading(true);
      setMessageWithType('', '');
      console.log('Attempting to log in with:', { username });
      const response = await axios.post(`${BACKEND_URL}/api/login`, { username, password }, { withCredentials: true });
      console.log('Login response:', response.data);
      if (response.data.success) {
        setUserId(response.data.userId);
        setMessageWithType('Logged in successfully!', 'success');
        setUsername('');
        setPassword('');
        setFormErrors({ username: '', email: '', password: '', newPassword: '' });
        if (onLogin) onLogin();

        // Verify the session is set correctly
        const checkRes = await axios.get(`${BACKEND_URL}/api/check-login`, { withCredentials: true });
        console.log('Post-login session check:', {
          userId: checkRes.data.userId,
          status: checkRes.status,
          headers: checkRes.headers,
        });
        if (!checkRes.data.userId) {
          console.log('Session not found after login, logging out');
          setUserId(null);
          setMessageWithType('Session lost after login. Please try again.', 'error');
          return;
        }

        navigate('/scheduler');
      } else {
        setMessageWithType(response.data.error || 'Login failed. Please check your credentials.', 'error');
      }
    } catch (error) {
      console.error('Error during login:', error);
      setMessageWithType(
        error.code === 'ERR_NETWORK'
          ? 'Cannot connect to the server. Please ensure the backend is running.'
          : error.response?.data?.error || 'Error logging in: Unable to connect to the server.',
        'error'
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Handle forgot password
  const handleForgotPassword = async (e) => {
    e.preventDefault();

    const emailError = validateEmail(email);

    setFormErrors({
      username: '',
      email: emailError,
      password: '',
      newPassword: '',
    });

    if (emailError) {
      setMessageWithType('Please fix the errors in the form.', 'error');
      return;
    }

    try {
      setIsLoading(true);
      setMessageWithType('', '');
      console.log('Sending forgot password request for:', { email });
      const response = await axios.post(`${BACKEND_URL}/api/forgot-password`, { email }, { withCredentials: true });
      console.log('Forgot password response:', response.data);
      if (response.data.success) {
        setMessageWithType('Password reset link sent! Check the console for the link.', 'success');
        setEmail('');
        setFormErrors({ username: '', email: '', password: '', newPassword: '' });
      } else {
        setMessageWithType(response.data.error || 'Failed to send reset link. Please try again.', 'error');
      }
    } catch (error) {
      console.error('Error during forgot-password:', error);
      setMessageWithType(
        error.code === 'ERR_NETWORK'
          ? 'Cannot connect to the server. Please ensure the backend is running.'
          : error.response?.data?.error || 'Error sending reset link: Unable to connect to the server.',
        'error'
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Handle password reset
  const handleResetPassword = async (e) => {
    e.preventDefault();

    const newPasswordError = validatePassword(newPassword);

    setFormErrors({
      username: '',
      email: '',
      password: '',
      newPassword: newPasswordError,
    });

    if (newPasswordError) {
      setMessageWithType('Please fix the errors in the form.', 'error');
      return;
    }

    try {
      setIsLoading(true);
      setMessageWithType('', '');
      console.log('Resetting password with token:', resetToken);
      const response = await axios.post(`${BACKEND_URL}/api/reset-password`, { token: resetToken, newPassword }, { withCredentials: true });
      console.log('Reset password response:', response.data);
      if (response.data.success) {
        setMessageWithType('Password reset successfully! Please log in.', 'success');
        setView('login');
        setNewPassword('');
        setResetToken('');
        setFormErrors({ username: '', email: '', password: '', newPassword: '' });
      } else {
        setMessageWithType(response.data.error || 'Failed to reset password. Please try again.', 'error');
      }
    } catch (error) {
      console.error('Error during reset-password:', error);
      setMessageWithType(
        error.code === 'ERR_NETWORK'
          ? 'Cannot connect to the server. Please ensure the backend is running.'
          : error.response?.data?.error || 'Error resetting password: Unable to connect to the server.',
        'error'
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Handle app logout
  const handleAppLogout = async () => {
    try {
      setIsLoading(true);
      setMessageWithType('', '');
      console.log('Logging out of app...');
      const response = await axios.post(`${BACKEND_URL}/api/logout`, {}, { withCredentials: true });
      console.log('Logout response:', response.data);
      if (response.data.success) {
        setUserId(null);
        setLoginStatus({ tiktok: false, instagram: false, facebook: false, twitter: false, youtube: false });
        setUserDetails({ tiktok: null, instagram: null, twitter: null, youtube: null });
        setValidationErrors({ tiktok: null, instagram: null, twitter: null, youtube: null });
        setNeedsReconnect({ tiktok: false, instagram: false, facebook: false, twitter: false, youtube: false });
        setAuthUrls({ tiktok: '', instagram: '', twitter: '', youtube: '' });
        setMessageWithType('Logged out of app successfully!', 'success');
        navigate('/login');
      } else {
        setMessageWithType('Failed to log out: ' + (response.data.error || 'Unknown error'), 'error');
      }
    } catch (error) {
      console.error('Error during logout:', error);
      setMessageWithType(
        error.code === 'ERR_NETWORK'
          ? 'Cannot connect to the server. Please ensure the backend is running.'
          : error.response?.data?.error || 'Error logging out: Unable to connect to the server.',
        'error'
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Validate file type
  const validateFileType = (file) => {
    const allowedImageTypes = ['image/jpeg', 'image/png', 'image/gif'];
    const allowedVideoTypes = ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/x-ms-wmv', 'video/mpeg'];
    const fileType = file.type;

    if (allowedImageTypes.includes(fileType)) {
      setMediaType('image');
      return true;
    } else if (allowedVideoTypes.includes(fileType)) {
      setMediaType('video');
      return true;
    } else {
      setMessageWithType('Unsupported file type. Allowed types: JPEG, PNG, GIF, MP4, MOV, AVI, WMV, MPEG', 'error');
      return false;
    }
  };

  // Handle drag-and-drop events
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files.length > 0 && validateFileType(files[0])) {
      if (previewMediaRef.current) {
        URL.revokeObjectURL(previewMediaRef.current);
      }
      const newPreviewUrl = URL.createObjectURL(files[0]);
      previewMediaRef.current = newPreviewUrl;
      setMediaFile(files[0]);
      setPreviewMedia(newPreviewUrl);
    }
  };

  // Handle file input change
  const handleFileChange = (e) => {
    if (e.target.files.length > 0 && validateFileType(e.target.files[0])) {
      if (previewMediaRef.current) {
        URL.revokeObjectURL(previewMediaRef.current);
      }
      const newPreviewUrl = URL.createObjectURL(e.target.files[0]);
      previewMediaRef.current = newPreviewUrl;
      setMediaFile(e.target.files[0]);
      setPreviewMedia(newPreviewUrl);
    }
  };

  // Handle removing the uploaded content
  const handleRemoveMedia = () => {
    if (previewMediaRef.current) {
      URL.revokeObjectURL(previewMediaRef.current);
      previewMediaRef.current = null;
    }
    setMediaFile(null);
    setPreviewMedia(null);
    setMediaType(null);
    setMediaUrl('');
    setImageEdits({ rotate: 0, filter: 'none', crop: { x: 0, y: 0, width: 100, height: 100 } });
    setVideoEdits({ start: 0, end: null });
    setVideoDuration(0);
    setMessageWithType('', '');
  };

  // Trigger file input click
  const handleUploadAreaClick = () => {
    if (!mediaFile) {
      fileInputRef.current.click();
    }
  };

  // Handle video duration
  const handleVideoLoadedMetadata = () => {
    if (videoRef.current) {
      setVideoDuration(videoRef.current.duration);
      setVideoEdits({ start: 0, end: videoRef.current.duration });
    }
  };

  // Image editing functions
  const handleRotate = (degrees) => {
    setImageEdits((prev) => ({ ...prev, rotate: (prev.rotate + degrees) % 360 }));
  };

  const handleFilter = (filter) => {
    setImageEdits((prev) => ({ ...prev, filter }));
  };

  // Video editing functions
  const handleTrimChange = (type, value) => {
    setVideoEdits((prev) => {
      const newEdits = { ...prev };
      newEdits[type] = parseFloat(value);
      if (newEdits.start >= newEdits.end) {
        newEdits.end = newEdits.start + 1;
      }
      if (newEdits.end > videoDuration) {
        newEdits.end = videoDuration;
      }
      return newEdits;
    });
  };

  // Format file size for display
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Upload media to server
  const handleMediaUpload = async () => {
    if (!mediaFile) {
      setMessageWithType('Please select a media file.', 'error');
      return;
    }
    const formData = new FormData();
    formData.append('media', mediaFile);
    try {
      setIsLoading(true);
      setMessageWithType('', '');
      console.log('Uploading media...');
      const response = await axios.post(`${BACKEND_URL}/api/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true,
      });
      console.log('Media upload response:', response.data);
      const relativeUrl = response.data.url;
      setMediaUrl(relativeUrl);
      setMessageWithType('Media uploaded successfully!', 'success');
    } catch (error) {
      console.error('Error uploading media:', error);
      setMessageWithType(
        error.code === 'ERR_NETWORK'
          ? 'Cannot connect to the server. Please ensure the backend is running.'
          : error.response?.data?.error || 'Error uploading media: Unable to connect to the server.',
        'error'
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Schedule post
  const handleSchedulePost = async () => {
    if (!mediaUrl || !scheduleTime || platforms.length === 0) {
      setMessageWithType('Please upload media, set a schedule time, and select at least one platform.', 'error');
      return;
    }
    if (platforms.includes('tiktok') && !loginStatus.tiktok) {
      setMessageWithType('Please authenticate with TikTok.', 'error');
      return;
    }
    if ((platforms.includes('instagram') || platforms.includes('facebook')) && !loginStatus.instagram) {
      setMessageWithType('Please authenticate with Instagram/Facebook.', 'error');
      return;
    }
    if (platforms.includes('twitter') && !loginStatus.twitter) {
      setMessageWithType('Please authenticate with Twitter.', 'error');
      return;
    }
    if (platforms.includes('youtube') && !loginStatus.youtube) {
      setMessageWithType('Please authenticate with YouTube.', 'error');
      return;
    }

    try {
      setIsLoading(true);
      setMessageWithType('', '');
      console.log('Scheduling post:', { userId, mediaUrl, caption, scheduleTime, platforms });
      const response = await axios.post(`${BACKEND_URL}/api/schedule`, {
        userId,
        mediaUrl,
        caption,
        scheduleTime,
        platforms,
      }, { withCredentials: true });
      console.log('Schedule post response:', response.data);
      setMessageWithType(response.data.message, 'success');
      handleRemoveMedia();
      setCaption('');
      setScheduleTime('');
      setPlatforms([]);
    } catch (error) {
      console.error('Error scheduling post:', error);
      setMessageWithType(
        error.code === 'ERR_NETWORK'
          ? 'Cannot connect to the server. Please ensure the backend is running.'
          : error.response?.data?.error || 'Error scheduling post: Unable to connect to the server.',
        'error'
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Logout from a specific platform
  const handlePlatformLogout = async (platform) => {
    try {
      setIsLoading(true);
      setMessageWithType('', '');
      console.log(`Attempting to disconnect from ${platform}...`);
      const response = await axios.post(`${BACKEND_URL}/api/${platform}/logout`, {}, { withCredentials: true });
      console.log('Platform logout response:', response.data);

      if (response.data.success) {
        const statusRes = await axios.get(`${BACKEND_URL}/api/check-platform-login`, { withCredentials: true });
        const newLoginStatus = statusRes.data.loginStatus || { tiktok: false, instagram: false, facebook: false, twitter: false, youtube: false };
        setLoginStatus(newLoginStatus);
        setUserDetails(statusRes.data.userDetails || { tiktok: null, instagram: null, twitter: null, youtube: null });
        setValidationErrors(statusRes.data.validationErrors || { tiktok: null, instagram: null, twitter: null, youtube: null });
        setNeedsReconnect(statusRes.data.needsReconnect || { tiktok: false, instagram: false, facebook: false, twitter: false, youtube: false });
        setMessageWithType(`Logged out from ${platform.charAt(0).toUpperCase() + platform.slice(1)} successfully.`, 'success');
      } else {
        setMessageWithType(`Failed to log out from ${platform}: ${response.data.error || 'Unknown error'}`, 'error');
      }
    } catch (error) {
      console.error(`Error logging out from ${platform}:`, error);
      setMessageWithType(
        error.code === 'ERR_NETWORK'
          ? 'Cannot connect to the server. Please ensure the backend is running.'
          : error.response?.data?.error || `Error logging out from ${platform}: Unable to connect to the server.`,
        'error'
      );
      try {
        const statusRes = await axios.get(`${BACKEND_URL}/api/check-platform-login`, { withCredentials: true });
        const newLoginStatus = statusRes.data.loginStatus || { tiktok: false, instagram: false, facebook: false, twitter: false, youtube: false };
        setLoginStatus(newLoginStatus);
        setUserDetails(statusRes.data.userDetails || { tiktok: null, instagram: null, twitter: null, youtube: null });
        setValidationErrors(statusRes.data.validationErrors || { tiktok: null, instagram: null, twitter: null, youtube: null });
        setNeedsReconnect(statusRes.data.needsReconnect || { tiktok: false, instagram: false, facebook: false, twitter: false, youtube: false });
      } catch (checkError) {
        console.error('Error checking login status after platform logout:', checkError);
        setMessageWithType(`Error checking login status after platform logout: ${checkError.message}`, 'error');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Render a platform connection item
  const renderPlatformItem = (platform, label, platformColor) => {
    const isConnected = loginStatus[platform];
    const authUrl = authUrls[platform];
    const userDetail = userDetails[platform];
    const validationError = validationErrors[platform];

    return (
      <div className={`platform-auth-item ${platform}`}>
        <span className="platform-label">{label}</span>
        <span className={`status-indicator ${isConnected ? 'connected' : 'disconnected'}`}>
          {isConnected ? `Connected as ${userDetail || 'Account'}` : 'Not Connected'}
        </span>
        {validationError && !isConnected && (
          <p className="validation-error">
            {validationError.includes('Missing required scope')
              ? `Please grant the required permissions to post to ${label}.`
              : validationError}
          </p>
        )}
        {isLoading || isAuthenticating ? (
          <button className="auth-button loading" disabled>
            Processing...
          </button>
        ) : isConnected ? (
          <button
            onClick={() => handlePlatformLogout(platform)}
            className={`auth-button ${platform} disconnect`}
          >
            Disconnect
          </button>
        ) : (
          <a
            href={authUrl || '#'}
            className={`auth-button ${platform} connect ${!authUrl ? 'disabled' : ''}`}
            onClick={(e) => {
              if (!authUrl) {
                e.preventDefault();
              } else {
                setIsAuthenticating(true);
              }
            }}
          >
            Connect
          </a>
        )}
      </div>
    );
  };

  return (
    <div className="scheduler">
      {userId ? (
        <>
          <div className="platform-container">
            <h3>Schedule a Post</h3>
            <button onClick={handleAppLogout} disabled={isLoading} className="logout-button">
              {isLoading ? 'Logging Out...' : 'Log Out of App'}
            </button>
            <div className="platform-auth-row">
              {renderPlatformItem('tiktok', 'TikTok', '#ff0050')}
              {renderPlatformItem('instagram', 'Instagram/FB', '#e1306c')}
              {renderPlatformItem('twitter', 'Twitter', '#1da1f2')}
              {renderPlatformItem('youtube', 'YouTube', '#ff0000')}
            </div>
            {message && <p className={`message ${messageType}`}>{message}</p>}
          </div>
          <div className="form-container">
            <div
              className={`upload-area ${isDragging ? 'dragging' : ''} ${mediaFile ? 'has-file' : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={handleUploadAreaClick}
            >
              <input
                type="file"
                accept=".jpg,.jpeg,.png,.gif,.mp4,.mov,.avi,.wmv,.mpeg"
                onChange={handleFileChange}
                ref={fileInputRef}
                style={{ display: 'none' }}
              />
              <div className="upload-content">
                <svg className="upload-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="17 8 12 3 7 8"></polyline>
                  <line x1="12" y1="3" x2="12" y2="15"></line>
                </svg>
                <p className="upload-text">
                  {mediaFile ? `Selected: ${mediaFile.name}` : 'Drag & drop your media here or click to upload'}
                </p>
              </div>
            </div>
            {previewMedia && (
              <div className="preview-section">
                <h4>Preview</h4>
                <div className="file-info">
                  <p><strong>Name:</strong> {mediaFile.name}</p>
                  <p><strong>Type:</strong> {mediaType === 'image' ? 'Image' : 'Video'}</p>
                  <p><strong>Size:</strong> {formatFileSize(mediaFile.size)}</p>
                </div>
                <div className="media-container">
                  {mediaType === 'image' ? (
                    <div className="image-preview">
                      <img
                        src={previewMedia}
                        alt="Preview"
                        style={{
                          transform: `rotate(${imageEdits.rotate}deg)`,
                          filter: imageEdits.filter,
                          maxWidth: '100%',
                          maxHeight: '300px',
                        }}
                      />
                      <button className="remove-button" onClick={handleRemoveMedia}>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M3 6h18"></path>
                          <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                          <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                          <line x1="10" y1="11" x2="10" y2="17"></line>
                          <line x1="14" y1="11" x2="14" y2="17"></line>
                        </svg>
                        Remove
                      </button>
                      <div className="edit-controls">
                        <button onClick={() => handleRotate(90)}>Rotate 90°</button>
                        <button onClick={() => handleRotate(-90)}>Rotate -90°</button>
                        <select onChange={(e) => handleFilter(e.target.value)} value={imageEdits.filter}>
                          <option value="none">No Filter</option>
                          <option value="grayscale(100%)">Grayscale</option>
                          <option value="sepia(100%)">Sepia</option>
                          <option value="brightness(150%)">Brighten</option>
                          <option value="contrast(150%)">Contrast</option>
                        </select>
                      </div>
                    </div>
                  ) : (
                    <div className="video-preview">
                      <video
                        ref={videoRef}
                        src={previewMedia}
                        controls
                        onLoadedMetadata={handleVideoLoadedMetadata}
                        style={{ maxWidth: '100%', maxHeight: '300px' }}
                      />
                      <button className="remove-button" onClick={handleRemoveMedia}>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M3 6h18"></path>
                          <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                          <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                          <line x1="10" y1="11" x2="10" y2="17"></line>
                          <line x1="14" y1="11" x2="14" y2="17"></line>
                        </svg>
                        Remove
                      </button>
                      <div className="edit-controls">
                        <label>
                          Start Time:
                          <input
                            type="number"
                            min="0"
                            max={videoDuration - 1}
                            value={videoEdits.start}
                            onChange={(e) => handleTrimChange('start', e.target.value)}
                            step="0.1"
                          />
                        </label>
                        <label>
                          End Time:
                          <input
                            type="number"
                            min={videoEdits.start + 1}
                            max={videoDuration}
                            value={videoEdits.end}
                            onChange={(e) => handleTrimChange('end', e.target.value)}
                            step="0.1"
                          />
                        </label>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            {mediaFile && !mediaUrl && (
              <button className="upload-submit-button" onClick={handleMediaUpload} disabled={isLoading}>
                {isLoading ? 'Uploading...' : 'Upload Media'}
              </button>
            )}
            {mediaUrl && <p className="media-url">Media URL: <a href={mediaUrl} target="_blank" rel="noopener noreferrer">{mediaUrl}</a></p>}
            <input
              type="text"
              placeholder="Caption"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              disabled={isLoading}
              className="form-input"
            />
            <input
              type="datetime-local"
              value={scheduleTime}
              onChange={(e) => setScheduleTime(e.target.value)}
              disabled={isLoading}
              className="form-input"
            />
            <div className="platform-select">
              <label>
                <input
                  type="checkbox"
                  checked={platforms.includes('tiktok')}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setPlatforms([...platforms, 'tiktok']);
                    } else {
                      setPlatforms(platforms.filter((p) => p !== 'tiktok'));
                    }
                  }}
                  disabled={!loginStatus.tiktok || (mediaType === 'image' && !['instagram', 'facebook', 'twitter'].some(p => platforms.includes(p))) || isLoading}
                />
                TikTok
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={platforms.includes('instagram')}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setPlatforms([...platforms, 'instagram']);
                    } else {
                      setPlatforms(platforms.filter((p) => p !== 'instagram'));
                    }
                  }}
                  disabled={!loginStatus.instagram || isLoading}
                />
                Instagram
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={platforms.includes('facebook')}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setPlatforms([...platforms, 'facebook']);
                    } else {
                      setPlatforms(platforms.filter((p) => p !== 'facebook'));
                    }
                  }}
                  disabled={!loginStatus.facebook || isLoading}
                />
                Facebook
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={platforms.includes('twitter')}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setPlatforms([...platforms, 'twitter']);
                    } else {
                      setPlatforms(platforms.filter((p) => p !== 'twitter'));
                    }
                  }}
                  disabled={!loginStatus.twitter || isLoading}
                />
                Twitter
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={platforms.includes('youtube')}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setPlatforms([...platforms, 'youtube']);
                    } else {
                      setPlatforms(platforms.filter((p) => p !== 'youtube'));
                    }
                  }}
                  disabled={!loginStatus.youtube || (mediaType === 'image' && !['instagram', 'facebook', 'twitter'].some(p => platforms.includes(p))) || isLoading}
                />
                YouTube
              </label>
            </div>
            <button className="schedule-button" onClick={handleSchedulePost} disabled={isLoading}>
              {isLoading ? 'Scheduling...' : 'Schedule Post'}
            </button>
            {message && <p className={`message ${messageType}`}>{message}</p>}
          </div>
        </>
      ) : (
        <div className="auth-container">
          {view === 'login' && (
            <div className="login-section">
              <h3>Log In to Your Account</h3>
              <form onSubmit={handleAppLogin}>
                <div className="form-group">
                  <label>Username:</label>
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => {
                      setUsername(e.target.value);
                      setFormErrors({ ...formErrors, username: validateUsername(e.target.value) });
                    }}
                    required
                    className={`form-input ${formErrors.username ? 'input-error' : ''}`}
                  />
                  {formErrors.username && <p className="error-text">{formErrors.username}</p>}
                </div>
                <div className="form-group">
                  <label>Password:</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setFormErrors({ ...formErrors, password: validatePassword(e.target.value) });
                    }}
                    required
                    className={`form-input ${formErrors.password ? 'input-error' : ''}`}
                  />
                  {formErrors.password && <p className="error-text">{formErrors.password}</p>}
                </div>
                <button type="submit" disabled={isLoading} className="form-button">
                  {isLoading ? 'Logging In...' : 'Log In'}
                </button>
              </form>
              <p className="form-link">
                Don't have an account?{' '}
                <button onClick={() => setView('register')} className="link-button">Register</button>
              </p>
              <p className="form-link">
                Forgot your password?{' '}
                <button onClick={() => setView('forgot-password')} className="link-button">Reset Password</button>
              </p>
              {message && <p className={`message ${messageType}`}>{message}</p>}
            </div>
          )}
          {view === 'register' && (
            <div className="register-section">
              <h3>Register a New Account</h3>
              <form onSubmit={handleRegister}>
                <div className="form-group">
                  <label>Username:</label>
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => {
                      setUsername(e.target.value);
                      setFormErrors({ ...formErrors, username: validateUsername(e.target.value) });
                    }}
                    required
                    className={`form-input ${formErrors.username ? 'input-error' : ''}`}
                  />
                  {formErrors.username && <p className="error-text">{formErrors.username}</p>}
                </div>
                <div className="form-group">
                  <label>Email:</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setFormErrors({ ...formErrors, email: validateEmail(e.target.value) });
                    }}
                    required
                    className={`form-input ${formErrors.email ? 'input-error' : ''}`}
                  />
                  {formErrors.email && <p className="error-text">{formErrors.email}</p>}
                </div>
                <div className="form-group">
                  <label>Password:</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setFormErrors({ ...formErrors, password: validatePassword(e.target.value) });
                    }}
                    required
                    className={`form-input ${formErrors.password ? 'input-error' : ''}`}
                  />
                  {formErrors.password && <p className="error-text">{formErrors.password}</p>}
                </div>
                <button type="submit" disabled={isLoading} className="form-button">
                  {isLoading ? 'Registering...' : 'Register'}
                </button>
              </form>
              <p className="form-link">
                Already have an account?{' '}
                <button onClick={() => setView('login')} className="link-button">Log In</button>
              </p>
              {message && <p className={`message ${messageType}`}>{message}</p>}
            </div>
          )}
          {view === 'forgot-password' && (
            <div className="forgot-password-section">
              <h3>Forgot Password</h3>
              <form onSubmit={handleForgotPassword}>
                <div className="form-group">
                  <label>Email:</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setFormErrors({ ...formErrors, email: validateEmail(e.target.value) });
                    }}
                    required
                    className={`form-input ${formErrors.email ? 'input-error' : ''}`}
                  />
                  {formErrors.email && <p className="error-text">{formErrors.email}</p>}
                </div>
                <button type="submit" disabled={isLoading} className="form-button">
                  {isLoading ? 'Sending...' : 'Send Reset Link'}
                </button>
              </form>
              <p className="form-link">
                Back to{' '}
                <button onClick={() => setView('login')} className="link-button">Log In</button>
              </p>
              {message && <p className={`message ${messageType}`}>{message}</p>}
            </div>
          )}
          {view === 'reset-password' && (
            <div className="reset-password-section">
              <h3>Reset Password</h3>
              <form onSubmit={handleResetPassword}>
                <div className="form-group">
                  <label>New Password:</label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => {
                      setNewPassword(e.target.value);
                      setFormErrors({ ...formErrors, newPassword: validatePassword(e.target.value) });
                    }}
                    required
                    className={`form-input ${formErrors.newPassword ? 'input-error' : ''}`}
                  />
                  {formErrors.newPassword && <p className="error-text">{formErrors.newPassword}</p>}
                </div>
                <button type="submit" disabled={isLoading} className="form-button">
                  {isLoading ? 'Resetting...' : 'Reset Password'}
                </button>
              </form>
              <p className="form-link">
                Back to{' '}
                <button onClick={() => setView('login')} className="link-button">Log In</button>
              </p>
              {message && <p className={`message ${messageType}`}>{message}</p>}
            </div>
          )}
        </div>
      )}
      <Footer /> {/* Use the imported Footer component */}
    </div>
  );
};

export default SocialMediaScheduler;